import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { calculateMatchStats } from '../services/jobMatchingService';
import '../styles/jobSearchResults.css';

const JobSearchResultsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [matchingJobs, setMatchingJobs] = useState([]);
  const [preferences, setPreferences] = useState(null);
  const [matchStats, setMatchStats] = useState(null);
  const [sortBy, setSortBy] = useState('match'); // 'match', 'distance', 'rate'

  useEffect(() => {
    // Get data from location state or localStorage
    const stateData = location.state;
    
    if (stateData?.matchingJobs) {
      setMatchingJobs(stateData.matchingJobs);
      setPreferences(stateData.preferences);
      setMatchStats(calculateMatchStats(stateData.matchingJobs));
    } else {
      // Try to load from localStorage as fallback
      const savedJobs = localStorage.getItem('matchingJobs');
      const savedPrefs = localStorage.getItem('matchingPreferences');
      
      if (savedJobs && savedPrefs) {
        const jobs = JSON.parse(savedJobs);
        const prefs = JSON.parse(savedPrefs);
        setMatchingJobs(jobs);
        setPreferences(prefs);
        setMatchStats(calculateMatchStats(jobs));
      } else {
        navigate('/');
      }
    }
  }, [location, navigate]);

  const handleJobClick = (jobId) => {
    localStorage.setItem('job', jobId);
    navigate(`/job-details/${jobId}`);
  };

  const handleApply = (jobId, e) => {
    e.stopPropagation();
    navigate('/job-details/' + jobId, { state: { scrollToApply: true } });
  };

  const getSortedJobs = () => {
    const jobsCopy = [...matchingJobs];

    switch (sortBy) {
      case 'distance':
        if (preferences?.location) {
          const userLat = preferences.location.latitude;
          const userLon = preferences.location.longitude;
          
          return jobsCopy.sort((a, b) => {
            const distA = Math.sqrt(Math.pow(a.location?.latitude - userLat, 2) + Math.pow(a.location?.longitude - userLon, 2));
            const distB = Math.sqrt(Math.pow(b.location?.latitude - userLat, 2) + Math.pow(b.location?.longitude - userLon, 2));
            return distA - distB;
          });
        }
        return jobsCopy;
      case 'rate':
        return jobsCopy.sort((a, b) => {
          const rateA = parseFloat(a.rate) || 0;
          const rateB = parseFloat(b.rate) || 0;
          return rateB - rateA;
        });
      case 'match':
      default:
        return jobsCopy.sort((a, b) => b.matchScore - a.matchScore);
    }
  };

  const getMatchQualityLabel = (percentage) => {
    if (percentage >= 80) return 'Excellent';
    if (percentage >= 60) return 'Good';
    if (percentage >= 40) return 'Fair';
    return 'Fair';
  };

  const getMatchQualityColor = (percentage) => {
    if (percentage >= 80) return 'excellent';
    if (percentage >= 60) return 'good';
    if (percentage >= 40) return 'fair';
    return 'low';
  };

  const sortedJobs = getSortedJobs();

  if (!matchingJobs || matchingJobs.length === 0) {
    return (
      <div className="search-results-page">
        <div className="results-container">
          <div className="no-results">
            <h2>No Matching Jobs Found</h2>
            <p>Try adjusting your search criteria or location radius</p>
            <button onClick={() => navigate('/')} className="btn-back">
              Back to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="search-results-page">
      <div className="results-container">
        <div className="results-header">
          <h1>Your Job Matches</h1>
          <p>Found <strong>{matchingJobs.length}</strong> matching opportunities</p>
        </div>

        {matchStats && (
          <div className="match-statistics">
            <div className="stat-card">
              <div className="stat-value">{matchStats.total}</div>
              <div className="stat-label">Total Matches</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{matchStats.averageScore}%</div>
              <div className="stat-label">Average Match</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{matchStats.topScore}%</div>
              <div className="stat-label">Best Match</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{matchStats.distribution.excellent}</div>
              <div className="stat-label">Excellent Matches</div>
            </div>
          </div>
        )}

        <div className="sort-controls">
          <label>Sort by:</label>
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
            <option value="match">Best Match</option>
            <option value="distance">Distance</option>
            <option value="rate">Highest Rate</option>
          </select>
        </div>

        <div className="jobs-results-grid">
          {sortedJobs.map((job) => (
            <div
              key={job.id}
              className="job-result-card"
              onClick={() => handleJobClick(job.id)}
            >
              <div className="job-header">
                <div className="job-image">
                  <img src={job.image || '/images/default-job.png'} alt={job.title} />
                </div>
                <div className="job-title-section">
                  <h3>{job.title}</h3>
                  <p className="company">{job.company}</p>
                </div>
                <div className={`match-badge match-${getMatchQualityColor(job.matchPercentage)}`}>
                  <div className="match-circle">
                    <div className="match-percentage">{job.matchPercentage}%</div>
                  </div>
                  <div className="match-label">{getMatchQualityLabel(job.matchPercentage)}</div>
                </div>
              </div>

              <div className="job-details">
                <div className="detail-item">
                  <span className="label">Rate:</span>
                  <span className="value">{job.rate}</span>
                </div>
                <div className="detail-item">
                  <span className="label">Type:</span>
                  <span className="badge">{job.type}</span>
                </div>
                <div className="detail-item">
                  <span className="label">Location:</span>
                  <span className="value">{job.location}</span>
                </div>
              </div>

              <p className="job-description">{job.description?.substring(0, 100)}...</p>

              <div className="job-footer">
                {job.skills && (
                  <div className="skills-tags">
                    {job.skills.slice(0, 3).map((skill, idx) => (
                      <span key={idx} className="skill-tag">{skill}</span>
                    ))}
                    {job.skills.length > 3 && <span className="more-skills">+{job.skills.length - 3}</span>}
                  </div>
                )}
                <button
                  className="btn-apply"
                  onClick={(e) => handleApply(job.id, e)}
                >
                  {job.matchPercentage >= 80 ? '⭐ Apply' : 'Apply'}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default JobSearchResultsPage;
